import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SegmentedPills from "../ui/SegmentedPills";
import { api, formatEURDecimal, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X, AlertTriangle, ShieldAlert, ArrowLeft, Wrench, Truck, Search, Gavel, Trash2 } from "lucide-react";
import { SkeletonTableRows } from "../ui/Skeleton";
import { confirmAction } from "../ConfirmDialog";
import EmptyState from "./EmptyState";
import { MAINT_TYPES, MAINT_STATUSES } from "./VehicleOpsTabs";
import GlobalFines from "./FinesView";
import SupplierPicker, { SUPPLIER_CATEGORIES, SUPPLIER_CATEGORY_LABELS } from "./SupplierPicker";

export const CLAIM_TYPE_LABELS = {
  accident: "Accident",
  vol: "Vol",
  bris_glace: "Bris de glace",
  carrosserie: "Dommage carrosserie",
  interieur: "Dommage intérieur",
  panne: "Panne importante",
  autre: "Autre",
};

export const CLAIM_STATUS_META = {
  open: { label: "Déclaré", cls: "bg-blue-100 text-blue-700" },
  in_progress: { label: "En cours", cls: "bg-amber-100 text-amber-700" },
  waiting_insurer: { label: "Attente assurance", cls: "bg-violet-100 text-violet-700" },
  closed: { label: "Clôturé", cls: "bg-slate-200 text-slate-600" },
};

const ISSUE_SEV = {
  low: { label: "Faible", cls: "bg-slate-100 text-slate-600" },
  medium: { label: "Moyenne", cls: "bg-amber-100 text-amber-700" },
  high: { label: "Haute", cls: "bg-red-100 text-red-700" },
};
const ISSUE_ST = {
  open: { label: "Ouvert", cls: "bg-red-50 text-red-700" },
  in_progress: { label: "En cours", cls: "bg-amber-50 text-amber-700" },
  resolved: { label: "Résolu", cls: "bg-emerald-50 text-emerald-700" },
};

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

export default function FleetEventsView({ vehicles = [], onBack, initialView }) {
  const navigate = useNavigate();
  const [view, setView] = useState(initialView || "issues");

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="fleet-events-view">
      {onBack && (
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-violet-700 transition-colors mb-4" data-testid="fleet-events-back-btn">
          <ArrowLeft className="h-3.5 w-3.5" /> Retour aux véhicules
        </button>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Événements flotte</h2>
        </div>
        <SegmentedPills
          value={view}
          onChange={setView}
          items={[
            { key: "issues", label: "Signalements", icon: AlertTriangle },
            { key: "claims", label: "Sinistres", icon: ShieldAlert },
            { key: "fines", label: "Contraventions", icon: Gavel },
            { key: "maintenance", label: "Interventions", icon: Wrench },
          ]}
          size="md"
          testid="fleet-events-toggle"
        />
      </div>
      <div className="mt-6">
        {view === "issues" ? <GlobalIssues navigate={navigate} vehicles={vehicles} /> : view === "claims" ? <GlobalClaims navigate={navigate} vehicles={vehicles} /> : view === "fines" ? <GlobalFines navigate={navigate} vehicles={vehicles} /> : <GlobalMaintenance navigate={navigate} vehicles={vehicles} />}
      </div>
    </div>
  );
}

function GlobalIssues({ navigate, vehicles = [], fixedVehicleId = null }) {
  const [issues, setIssues] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [vehicleId, setVehicleId] = useState(fixedVehicleId || "");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vendor/issues").then((r) => setIssues(r.data.issues || [])).catch(() => setIssues([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const vid = fixedVehicleId || vehicleId;
    if (!vid) { toast.error("Sélectionnez un véhicule"); return; }
    if (!title.trim()) { toast.error("Le titre est obligatoire"); return; }
    setSaving(true);
    try {
      await api.post(`/vendor/vehicles/${vid}/issues`, { title: title.trim() });
      toast.success("Signalement créé");
      setTitle(""); setVehicleId(fixedVehicleId || ""); setShowForm(false);
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const remove = async (i) => {
    const ok = await confirmAction({
      title: "Supprimer définitivement ce signalement ?",
      message: `« ${i.title} » sera supprimé de la flotte et de la fiche véhicule. Cette action est irréversible.`,
      confirmLabel: "Supprimer définitivement", variant: "danger", requireText: "SUPPRIMER",
    });
    if (!ok) return;
    try {
      await api.delete(`/vendor/vehicles/${i.vehicle_id}/issues/${i.id}`);
      toast.success("Signalement supprimé");
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  const base = useMemo(() => {
    if (!issues) return [];
    return fixedVehicleId ? issues.filter((i) => i.vehicle_id === fixedVehicleId) : issues;
  }, [issues, fixedVehicleId]);
  const filtered = useMemo(() => statusFilter === "all" ? base : base.filter((i) => i.status === statusFilter), [base, statusFilter]);

  if (issues === null) return <SkeletonTableRows rows={5} cols={5} testid="issues-skeleton" />;

  return (
    <div className="space-y-4" data-testid="global-issues">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedPills
          value={statusFilter}
          onChange={setStatusFilter}
          items={[
            { key: "all", label: "Tous", count: base.length },
            { key: "open", label: "Ouverts", count: base.filter((i) => i.status === "open").length },
            { key: "in_progress", label: "En cours", count: base.filter((i) => i.status === "in_progress").length },
            { key: "resolved", label: "Résolus", count: base.filter((i) => i.status === "resolved").length },
          ]}
          testid="issues-filter"
        />
        <button onClick={() => setShowForm(!showForm)} className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700" data-testid="add-issue-global-btn">
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Nouveau signalement"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-3 gap-3" data-testid="issue-global-form">
          {!fixedVehicleId && (
            <div>
              <label className={labelCls}>Véhicule *</label>
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} required className={inputCls + " mt-1"} data-testid="issue-global-vehicle">
                <option value="">Sélectionner…</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          )}
          <div className={fixedVehicleId ? "sm:col-span-2" : "sm:col-span-2"}>
            <label className={labelCls}>Titre *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Rayure portière avant droite" className={inputCls + " mt-1"} data-testid="issue-global-title" />
          </div>
          <div className="sm:col-span-3 flex justify-end">
            <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="issue-global-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Créer le signalement
            </button>
          </div>
        </form>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={AlertTriangle} title="Aucun signalement" description="Créez un signalement avec le bouton ci-dessus." testid="issues-empty" />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Référence</th>
                  <th className="text-left px-5 py-3 font-semibold">Titre</th>
                  {!fixedVehicleId && <th className="text-left px-5 py-3 font-semibold">Véhicule</th>}
                  <th className="text-left px-5 py-3 font-semibold">Statut</th>
                  <th className="text-left px-5 py-3 font-semibold">Créé le</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((i) => {
                  const st = ISSUE_ST[i.status] || ISSUE_ST.open;
                  return (
                    <tr key={i.id} className="hover:bg-violet-50/40 transition-colors" data-testid={`global-issue-row-${i.id}`}>
                      <td className="px-5 py-3.5 whitespace-nowrap text-xs font-bold text-slate-400 cursor-pointer" onClick={() => navigate(`/vendor/pilotage?tab=vehicles&vehicle=${i.vehicle_id}`)}>{i.reference || "—"}</td>
                      <td className="px-5 py-3.5 max-w-[240px] cursor-pointer" onClick={() => navigate(`/vendor/pilotage?tab=vehicles&vehicle=${i.vehicle_id}`)}>
                        <span className="font-semibold text-slate-900 truncate block">{i.title}</span>
                        {i.converted_claim_id && <span className="text-[10px] font-bold text-violet-600">Converti en {i.claim_reference}</span>}
                      </td>
                      {!fixedVehicleId && <td className="px-5 py-3.5 max-w-[160px] truncate text-slate-600">{i.vehicle_name}</td>}
                      <td className="px-5 py-3.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${st.cls}`}>{st.label}</span></td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500 text-xs">{formatDateFR(i.created_at?.split("T")[0])}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={(e) => { e.stopPropagation(); remove(i); }} className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 inline-flex items-center justify-center" title="Supprimer définitivement" data-testid={`issue-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_CLAIM = { vehicle_id: "", claim_type: "accident", date_of_loss: "", description: "", estimated_amount: "", responsibility: "unknown" };

const EMPTY_MAINT_GLOBAL = { vehicle_id: "", type: "entretien", title: "", date: "", status: "planned", estimated_cost: "", provider: "", supplier_id: null };

const EMPTY_SUPPLIER = { name: "", trade_name: "", category: "garage", phone: "", email: "", contact_name: "" };

function GlobalSuppliers({ navigate }) {
  const [suppliers, setSuppliers] = useState(null);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_SUPPLIER);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/vendor/suppliers?include_archived=true`).then((r) => setSuppliers(r.data.suppliers || [])).catch(() => setSuppliers([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/vendor/suppliers", form);
      toast.success(`Fournisseur ${data.supplier.reference} créé`);
      navigate(`/vendor/pilotage?tab=vehicles&supplier=${data.supplier.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const filtered = useMemo(() => {
    if (!suppliers) return [];
    let list = suppliers.filter((s) => showArchived || !s.archived);
    const n = search.trim().toLowerCase();
    if (n) list = list.filter((s) => [s.name, s.trade_name, s.category, s.reference, s.contact_name, s.email].some((f) => (f || "").toLowerCase().includes(n)));
    return list;
  }, [suppliers, search, showArchived]);

  if (suppliers === null) return <SkeletonTableRows rows={5} cols={5} testid="suppliers-skeleton" />;

  return (
    <div className="space-y-4" data-testid="global-suppliers">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un fournisseur…" className={inputCls + " pl-9"} data-testid="suppliers-search" />
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-violet-600" data-testid="suppliers-show-archived" />
          Archivés
        </label>
        <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700" data-testid="add-supplier-btn">
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Nouveau fournisseur"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-3 gap-3" data-testid="supplier-form">
          <div>
            <label className={labelCls}>Raison sociale *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Garage Martin SARL" className={inputCls + " mt-1"} data-testid="supplier-name" />
          </div>
          <div>
            <label className={labelCls}>Nom commercial</label>
            <input value={form.trade_name} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} className={inputCls + " mt-1"} data-testid="supplier-trade-name" />
          </div>
          <div>
            <label className={labelCls}>Catégorie</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls + " mt-1"} data-testid="supplier-category">
              {SUPPLIER_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Téléphone</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls + " mt-1"} data-testid="supplier-phone" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls + " mt-1"} data-testid="supplier-email" />
          </div>
          <div>
            <label className={labelCls}>Contact principal</label>
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls + " mt-1"} data-testid="supplier-contact" />
          </div>
          <div className="sm:col-span-3 flex justify-end">
            <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="supplier-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Créer le fournisseur
            </button>
          </div>
        </form>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={Truck}
          title="Aucun fournisseur"
          description={search ? "Aucun résultat pour cette recherche." : "Créez votre premier garage, carrossier ou prestataire."}
          testid="suppliers-empty"
        />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Référence</th>
                  <th className="text-left px-5 py-3 font-semibold">Fournisseur</th>
                  <th className="text-left px-5 py-3 font-semibold">Catégorie</th>
                  <th className="text-left px-5 py-3 font-semibold">Contact</th>
                  <th className="text-left px-5 py-3 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s) => (
                  <tr key={s.id} onClick={() => navigate(`/vendor/pilotage?tab=vehicles&supplier=${s.id}`)} className="cursor-pointer hover:bg-violet-50/40 transition-colors" data-testid={`supplier-row-${s.reference}`}>
                    <td className="px-5 py-3.5 whitespace-nowrap text-xs font-bold text-slate-400">{s.reference}</td>
                    <td className="px-5 py-3.5 max-w-[240px]">
                      <span className="font-semibold text-slate-900 truncate block">{s.trade_name || s.name}</span>
                      {s.trade_name && <span className="text-[10px] text-slate-500">{s.name}</span>}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-slate-600">{SUPPLIER_CATEGORY_LABELS[s.category] || s.category}</td>
                    <td className="px-5 py-3.5 max-w-[200px] truncate text-slate-600 text-xs">{s.contact_name || s.email || s.phone || "—"}</td>
                    <td className="px-5 py-3.5">
                      {s.archived ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 text-slate-500">Archivé</span>
                        : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">Actif</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function GlobalMaintenance({ navigate, vehicles }) {
  const [records, setRecords] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_MAINT_GLOBAL);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vendor/maintenance").then((r) => setRecords(r.data.records || [])).catch(() => setRecords([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error("Sélectionnez un véhicule"); return; }
    setSaving(true);
    try {
      await api.post(`/vendor/vehicles/${form.vehicle_id}/maintenance`, {
        type: form.type,
        title: form.title.trim(),
        date: form.date,
        status: form.status,
        estimated_cost: form.estimated_cost === "" ? null : parseFloat(form.estimated_cost),
        provider: form.provider.trim(),
        supplier_id: form.supplier_id || null,
      });
      toast.success("Intervention créée");
      setForm(EMPTY_MAINT_GLOBAL);
      setShowForm(false);
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const filtered = useMemo(() => {
    if (!records) return [];
    if (statusFilter === "all") return records;
    return records.filter((r) => (r.status || "done") === statusFilter);
  }, [records, statusFilter]);

  const remove = async (r) => {
    const ok = await confirmAction({
      title: "Supprimer définitivement cette intervention ?",
      message: `« ${r.title} » sera supprimée de la flotte et de la fiche véhicule. Cette action est irréversible.`,
      confirmLabel: "Supprimer définitivement", variant: "danger", requireText: "SUPPRIMER",
    });
    if (!ok) return;
    try {
      await api.delete(`/vendor/vehicles/${r.vehicle_id}/maintenance/${r.id}`);
      toast.success("Intervention supprimée"); load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  if (records === null) return <SkeletonTableRows rows={5} cols={5} testid="maintenance-skeleton" />;

  const countOf = (s) => records.filter((r) => (r.status || "done") === s).length;

  return (
    <div className="space-y-4" data-testid="global-maintenance">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedPills
          value={statusFilter}
          onChange={setStatusFilter}
          items={[
            { key: "all", label: "Toutes", count: records.length },
            { key: "to_plan", label: "À planifier", count: countOf("to_plan") },
            { key: "planned", label: "Planifiées", count: countOf("planned") },
            { key: "in_progress", label: "En cours", count: countOf("in_progress") },
            { key: "done", label: "Terminées", count: countOf("done") },
            { key: "cancelled", label: "Annulées", count: countOf("cancelled") },
          ]}
          testid="maintenance-filter"
        />
        <button
          onClick={() => setShowForm(!showForm)}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700"
          data-testid="add-maintenance-global-btn"
        >
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Nouvelle intervention"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-3 gap-3" data-testid="maintenance-global-form">
          <div>
            <label className={labelCls}>Véhicule *</label>
            <select value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} required className={inputCls + " mt-1"} data-testid="maint-global-vehicle">
              <option value="">Sélectionner…</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-global-type">
              {MAINT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Date prévue *</label>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className={inputCls + " mt-1"} data-testid="maint-global-date" />
          </div>
          <div>
            <label className={labelCls}>Intitulé *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Vidange + filtres" className={inputCls + " mt-1"} data-testid="maint-global-title" />
          </div>
          <div>
            <label className={labelCls}>Statut</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-global-status">
              {Object.entries(MAINT_STATUSES).filter(([v]) => v !== "done" && v !== "cancelled").map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Coût estimé (€)</label>
            <input type="number" min="0" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} className={inputCls + " mt-1"} data-testid="maint-global-estimated" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Fournisseur / garage</label>
            <div className="mt-1">
              <SupplierPicker
                supplierId={form.supplier_id}
                provider={form.provider}
                onChange={({ supplier_id, provider }) => setForm({ ...form, supplier_id, provider })}
                testid="maint-global-supplier"
              />
            </div>
          </div>
          <div className="sm:col-span-3 flex justify-end">
            <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="maint-global-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Créer l'intervention
            </button>
          </div>
        </form>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title="Aucune intervention"
          description={statusFilter !== "all" ? "Aucune intervention pour ce filtre." : "Les entretiens et réparations planifiés apparaîtront ici."}
          testid="maintenance-empty"
        />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Référence</th>
                  <th className="text-left px-5 py-3 font-semibold">Intervention</th>
                  <th className="text-left px-5 py-3 font-semibold">Véhicule</th>
                  <th className="text-left px-5 py-3 font-semibold">Prévue le</th>
                  <th className="text-left px-5 py-3 font-semibold">Statut</th>
                  <th className="text-right px-5 py-3 font-semibold">Coût</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r) => {
                  const st = MAINT_STATUSES[r.status || "done"] || MAINT_STATUSES.done;
                  return (
                    <tr
                      key={r.id}
                      className="hover:bg-violet-50/40 transition-colors"
                      data-testid={`global-maint-row-${r.id}`}
                    >
                      <td className="px-5 py-3.5 whitespace-nowrap text-xs font-bold text-slate-400 cursor-pointer" onClick={() => navigate(`/vendor/pilotage?tab=vehicles&vehicle=${r.vehicle_id}`)}>{r.reference || "—"}</td>
                      <td className="px-5 py-3.5 max-w-[240px] cursor-pointer" onClick={() => navigate(`/vendor/pilotage?tab=vehicles&vehicle=${r.vehicle_id}`)}>
                        <span className="font-semibold text-slate-900 truncate block">{r.title}</span>
                        <span className="text-[10px] text-slate-500">{MAINT_TYPES.find((t) => t.value === r.type)?.label || r.type}</span>
                      </td>
                      <td className="px-5 py-3.5 max-w-[160px] truncate text-slate-600">{r.vehicle_name}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500 text-xs">{formatDateFR(r.date)}</td>
                      <td className="px-5 py-3.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${st.cls}`}>{st.label}</span></td>
                      <td className="px-5 py-3.5 text-right font-semibold text-slate-900">
                        {(r.cost != null && r.cost !== 0) ? formatEURDecimal(r.cost) : (r.estimated_cost != null && r.estimated_cost !== 0) ? `≈ ${formatEURDecimal(r.estimated_cost)}` : "—"}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 inline-flex items-center justify-center" title="Supprimer définitivement" data-testid={`maint-global-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function GlobalClaims({ navigate, vehicles, fixedVehicleId = null }) {
  const [claims, setClaims] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(() => ({ ...EMPTY_CLAIM, vehicle_id: fixedVehicleId || "" }));
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vendor/claims").then((r) => setClaims(r.data.claims || [])).catch(() => setClaims([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const vid = fixedVehicleId || form.vehicle_id;
    if (!vid) { toast.error("Sélectionnez un véhicule"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/vendor/claims", {
        ...form, vehicle_id: vid,
        date_of_loss: form.date_of_loss || null,
        estimated_amount: form.estimated_amount === "" ? null : parseFloat(form.estimated_amount),
      });
      toast.success(`Sinistre ${data.claim.reference} créé`);
      if (fixedVehicleId) { setForm({ ...EMPTY_CLAIM, vehicle_id: fixedVehicleId }); setShowForm(false); load(); }
      else navigate(`/vendor/pilotage?tab=vehicles&claim=${data.claim.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const remove = async (c) => {
    const ok = await confirmAction({
      title: "Supprimer définitivement ce sinistre ?",
      message: `Le sinistre ${c.reference} sera supprimé de la flotte et de la fiche véhicule. Cette action est irréversible.`,
      confirmLabel: "Supprimer définitivement", variant: "danger", requireText: "SUPPRIMER",
    });
    if (!ok) return;
    try {
      await api.delete(`/vendor/claims/${c.id}`);
      toast.success("Sinistre supprimé"); load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  const filtered = useMemo(() => {
    if (!claims) return [];
    let list = fixedVehicleId ? claims.filter((c) => c.vehicle_id === fixedVehicleId) : claims;
    if (statusFilter !== "all") list = list.filter((c) => c.status === statusFilter);
    return list;
  }, [claims, statusFilter, fixedVehicleId]);

  if (claims === null) return <SkeletonTableRows rows={5} cols={5} testid="claims-skeleton" />;

  return (
    <div className="space-y-4" data-testid="global-claims">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedPills
          value={statusFilter}
          onChange={setStatusFilter}
          items={[
            { key: "all", label: "Tous", count: claims.length },
            { key: "open", label: "Déclarés", count: claims.filter((c) => c.status === "open").length },
            { key: "in_progress", label: "En cours", count: claims.filter((c) => c.status === "in_progress").length },
            { key: "waiting_insurer", label: "Assurance", count: claims.filter((c) => c.status === "waiting_insurer").length },
            { key: "closed", label: "Clôturés", count: claims.filter((c) => c.status === "closed").length },
          ]}
          testid="claims-filter"
        />
        <button
          onClick={() => setShowForm(!showForm)}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700"
          data-testid="add-claim-btn"
        >
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Nouveau sinistre"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-3 gap-3" data-testid="claim-form">
          {!fixedVehicleId && (
          <div>
            <label className={labelCls}>Véhicule *</label>
            <select value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} required className={inputCls + " mt-1"} data-testid="claim-vehicle">
              <option value="">Sélectionner…</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          )}
          <div>
            <label className={labelCls}>Type de sinistre</label>
            <select value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-type">
              {Object.entries(CLAIM_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Date du sinistre</label>
            <input type="date" value={form.date_of_loss} onChange={(e) => setForm({ ...form, date_of_loss: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-date" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Circonstances, dégâts constatés…" className={inputCls + " mt-1"} data-testid="claim-description" />
          </div>
          <div>
            <label className={labelCls}>Montant estimé (€)</label>
            <input type="number" min="0" step="0.01" value={form.estimated_amount} onChange={(e) => setForm({ ...form, estimated_amount: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-estimated" />
          </div>
          <div className="sm:col-span-3 flex justify-end">
            <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="claim-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Déclarer le sinistre
            </button>
          </div>
        </form>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={ShieldAlert}
          title="Aucun sinistre"
          description={statusFilter !== "all" ? "Aucun sinistre pour ce filtre." : "Les accidents, bris de glace et vols déclarés apparaîtront ici."}
          testid="claims-empty"
        />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Référence</th>
                  <th className="text-left px-5 py-3 font-semibold">Type</th>
                  {!fixedVehicleId && <th className="text-left px-5 py-3 font-semibold">Véhicule</th>}
                  <th className="text-left px-5 py-3 font-semibold">Date</th>
                  <th className="text-left px-5 py-3 font-semibold">Statut</th>
                  <th className="text-right px-5 py-3 font-semibold">Estimé</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => {
                  const st = CLAIM_STATUS_META[c.status] || CLAIM_STATUS_META.open;
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-violet-50/40 transition-colors"
                      data-testid={`claim-row-${c.reference}`}
                    >
                      <td className="px-5 py-3.5 whitespace-nowrap font-semibold text-slate-900 cursor-pointer" onClick={() => navigate(`/vendor/pilotage?tab=vehicles&claim=${c.id}`)}>{c.reference}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-600 cursor-pointer" onClick={() => navigate(`/vendor/pilotage?tab=vehicles&claim=${c.id}`)}>{CLAIM_TYPE_LABELS[c.claim_type] || c.claim_type}</td>
                      {!fixedVehicleId && <td className="px-5 py-3.5 max-w-[160px] truncate text-slate-600">{c.vehicle_name}</td>}
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500 text-xs">{formatDateFR(c.date_of_loss)}</td>
                      <td className="px-5 py-3.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${st.cls}`}>{st.label}</span></td>
                      <td className="px-5 py-3.5 text-right font-semibold text-slate-900">{c.estimated_amount != null ? formatEURDecimal(c.estimated_amount) : "—"}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={(e) => { e.stopPropagation(); remove(c); }} className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 inline-flex items-center justify-center" title="Supprimer définitivement" data-testid={`claim-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
